import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Calendar } from "../components/ui/calendar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import {
    CalendarDays,
    Clock,
    User,
    Phone,
    CheckCircle2,
    ArrowLeft,
    ArrowRight,
    Loader2,
    Stethoscope,
    Ear,
    Bandage,
    Activity,
    Droplets,
    Home,
    ClipboardList,
    Syringe,
    MapPin,
    FileDown,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPANISH_DAYS = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const SPANISH_MONTHS = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// Icon mapping for known procedure ids; falls back to ClipboardList for new ones added later.
const PROCEDURE_ICONS = {
    lavado_oidos: Ear,
    curacion: Bandage,
    signos_vitales: Activity,
    atencion_estomas: Droplets,
    higiene_bano_domicilio: Home,
    aplicacion_medicamento_iv: Syringe,
};

function formatPrice(p) {
    if (p == null) return null;
    return `$${Number(p).toLocaleString("es-MX")} MXN`;
}

function formatDateLong(date) {
    if (!date) return "";
    const day = date.getDate();
    const month = SPANISH_MONTHS[date.getMonth()];
    const weekday = SPANISH_DAYS[date.getDay()];
    const year = date.getFullYear();
    return `${weekday}, ${day} de ${month} de ${year}`;
}

function toISODate(date) {
    if (!date) return null;
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function formatTime12(t) {
    if (!t) return "";
    const [hh, mm] = t.split(":").map(Number);
    const period = hh >= 12 ? "PM" : "AM";
    const h12 = hh % 12 === 0 ? 12 : hh % 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${period}`;
}

const Stepper = ({ step }) => {
    const items = [
        { n: 1, label: "Procedimiento" },
        { n: 2, label: "Fecha" },
        { n: 3, label: "Hora" },
        { n: 4, label: "Datos" },
    ];
    return (
        <div
            className="flex items-center justify-center gap-1.5 sm:gap-3 mb-8 flex-wrap"
            data-testid="booking-stepper"
        >
            {items.map((it, idx) => {
                const active = step === it.n;
                const done = step > it.n;
                return (
                    <React.Fragment key={it.n}>
                        <div
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all ${
                                active
                                    ? "bg-primary text-primary-foreground"
                                    : done
                                    ? "bg-secondary text-secondary-foreground"
                                    : "bg-muted text-muted-foreground"
                            }`}
                            data-testid={`stepper-${it.n}`}
                        >
                            <span
                                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                                    active
                                        ? "bg-primary-foreground/20"
                                        : done
                                        ? "bg-secondary-foreground/10"
                                        : "bg-foreground/5"
                                }`}
                            >
                                {done ? "✓" : it.n}
                            </span>
                            <span className="hidden sm:inline font-medium">{it.label}</span>
                        </div>
                        {idx < items.length - 1 && (
                            <div className="h-px w-4 sm:w-8 bg-border" aria-hidden />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
};

// Optional promotional media shown inside a procedure card
const PROCEDURE_MEDIA = {
    lavado_oidos: "/lavado-oidos.jpg",
};

// ---- Ad attribution (Meta Ads) ----
const ATTRIBUTION_KEY = "cf_attribution";
const META_SOURCES = ["facebook", "fb", "meta", "instagram", "ig", "meta_ads"];

const captureAttribution = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const utmSource = (params.get("utm_source") || "").toLowerCase();
        const fbclid = params.get("fbclid");
        const utmCampaign = params.get("utm_campaign") || "";
        const isMetaAd = Boolean(fbclid) || META_SOURCES.includes(utmSource);
        if (isMetaAd) {
            sessionStorage.setItem(
                ATTRIBUTION_KEY,
                JSON.stringify({ source: "meta_ads", utm_campaign: utmCampaign })
            );
        }
    } catch (e) {
        /* sessionStorage unavailable */
    }
};

const getAttribution = () => {
    try {
        const raw = sessionStorage.getItem(ATTRIBUTION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
};

const ProcedureSelector = ({ procedures, selectedId, onSelect, loading }) => {
    if (loading) {
        return (
            <div
                className="flex items-center justify-center py-16 text-muted-foreground"
                data-testid="procedures-loading"
            >
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Cargando procedimientos...
            </div>
        );
    }
    if (!procedures || procedures.length === 0) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                No hay procedimientos disponibles por el momento.
            </div>
        );
    }
    return (
        <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3"
            data-testid="procedures-grid"
        >
            {procedures.map((p) => {
                const Icon = PROCEDURE_ICONS[p.id] || ClipboardList;
                const active = selectedId === p.id;
                return (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => onSelect(p.id)}
                        data-testid={`procedure-${p.id}`}
                        className={`text-left rounded-2xl border p-5 transition-all hover:-translate-y-0.5 ${
                            active
                                ? "bg-primary/5 border-primary ring-2 ring-primary shadow-md"
                                : "bg-background border-border hover:border-primary/50"
                        }`}
                    >
                        {PROCEDURE_MEDIA[p.id] && (
                            <img
                                src={PROCEDURE_MEDIA[p.id]}
                                alt={p.name}
                                className="w-full max-h-72 object-contain rounded-xl mb-4 bg-secondary/20 pointer-events-none"
                                data-testid={`procedure-media-${p.id}`}
                            />
                        )}
                        {!PROCEDURE_MEDIA[p.id] && (
                            <div
                                className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors ${
                                    active ? "bg-primary text-primary-foreground" : "bg-secondary/40 text-primary"
                                }`}
                            >
                                <Icon className="h-5 w-5" strokeWidth={1.6} />
                            </div>
                        )}
                        <p className="font-heading text-base sm:text-lg font-medium leading-tight">
                            {p.name}
                        </p>
                        {p.description && (
                            <p
                                className="mt-1.5 text-xs text-muted-foreground leading-snug"
                                data-testid={`procedure-description-${p.id}`}
                            >
                                {p.description}
                            </p>
                        )}
                        {p.price != null && (
                            <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-accent/40 px-3 py-1 text-xs font-medium text-foreground">
                                {formatPrice(p.price)}
                            </p>
                        )}
                        {p.promo_price != null && (
                            <p
                                className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-800"
                                data-testid={`procedure-promo-${p.id}`}
                            >
                                🔥 ${p.promo_price} de {formatTime12(p.promo_start)} a {formatTime12(p.promo_end)}
                            </p>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

const TimeSlotGrid = ({ availability, selectedTime, onSelect, loading, promo }) => {
    if (loading) {
        return (
            <div
                className="flex items-center justify-center py-16 text-muted-foreground"
                data-testid="slots-loading"
            >
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Cargando horarios disponibles...
            </div>
        );
    }
    if (!availability) return null;
    if (!availability.is_open) {
        return (
            <div
                className="text-center py-12 px-6 rounded-2xl bg-muted/60 border border-border"
                data-testid="slots-closed"
            >
                <p className="font-heading text-xl mb-2">El consultorio está cerrado</p>
                <p className="text-muted-foreground">
                    Los domingos no atendemos. Por favor selecciona otra fecha.
                </p>
            </div>
        );
    }

    const taken = new Set(availability.taken_slots);
    const slots = availability.all_slots;

    return (
        <div
            className="grid grid-cols-3 sm:grid-cols-4 gap-2.5"
            data-testid="time-slots-grid"
        >
            {slots.map((slot) => {
                const isTaken = taken.has(slot);
                const isActive = selectedTime === slot;
                const isPromo =
                    !isTaken && promo && slot >= promo.start && slot < promo.end;
                return (
                    <button
                        key={slot}
                        type="button"
                        disabled={isTaken}
                        onClick={() => onSelect(slot)}
                        data-testid={`time-slot-${slot}`}
                        className={`relative px-3 py-3 rounded-xl text-sm font-medium transition-all border ${
                            isActive
                                ? "bg-primary text-primary-foreground border-primary shadow-md -translate-y-0.5"
                                : isTaken
                                ? "bg-muted/40 text-muted-foreground border-transparent line-through cursor-not-allowed"
                                : isPromo
                                ? "bg-amber-50 text-foreground border-amber-300 hover:border-amber-500 hover:-translate-y-0.5"
                                : "bg-background text-foreground border-border hover:border-primary hover:bg-secondary/30 hover:-translate-y-0.5"
                        }`}
                    >
                        {formatTime12(slot)}
                        {isPromo && (
                            <span
                                className={`block text-[10px] font-semibold leading-tight mt-0.5 ${
                                    isActive ? "text-primary-foreground/90" : "text-amber-700"
                                }`}
                                data-testid={`promo-badge-${slot}`}
                            >
                                🔥 ${promo.price}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default function Booking() {
    const [step, setStep] = useState(1);
    const [procedures, setProcedures] = useState([]);
    const [schedule, setSchedule] = useState(null);
    const [loadingProcedures, setLoadingProcedures] = useState(true);
    const [selectedProcedureId, setSelectedProcedureId] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [loadingAvail, setLoadingAvail] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");

    // Auto-format Mexican phone as the user types: 444 123 4567
    const handlePhoneChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
        let formatted = digits;
        if (digits.length > 6) {
            formatted = `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        } else if (digits.length > 3) {
            formatted = `${digits.slice(0, 3)} ${digits.slice(3)}`;
        }
        setPhone(formatted);
    };
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState(null);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);

    const todayISO = useMemo(() => toISODate(new Date()), []);

    const selectedProcedure = useMemo(
        () => procedures.find((p) => p.id === selectedProcedureId) || null,
        [procedures, selectedProcedureId]
    );

    const promo = useMemo(() => {
        if (!selectedProcedure || selectedProcedure.promo_price == null) return null;
        return {
            price: selectedProcedure.promo_price,
            start: selectedProcedure.promo_start,
            end: selectedProcedure.promo_end,
        };
    }, [selectedProcedure]);

    const isPromoSlot =
        promo && selectedTime && selectedTime >= promo.start && selectedTime < promo.end;
    const effectivePrice = isPromoSlot ? promo.price : selectedProcedure?.price;

    const promoProcedures = useMemo(
        () => procedures.filter((p) => p.promo_price != null),
        [procedures]
    );

    // Load procedures + schedule on mount
    useEffect(() => {
        captureAttribution();
        const loadProcedures = async () => {
            try {
                const res = await axios.get(`${API}/procedures`);
                setProcedures(res.data || []);
            } catch (e) {
                console.error(e);
                toast.error("No se pudieron cargar los procedimientos.");
            } finally {
                setLoadingProcedures(false);
            }
        };
        loadProcedures();
        axios
            .get(`${API}/schedule`)
            .then((res) => setSchedule(res.data))
            .catch(() => {});
    }, []);

    // Load availability when selectedDate changes
    useEffect(() => {
        const loadAvailability = async () => {
            if (!selectedDate) return;
            setLoadingAvail(true);
            setAvailability(null);
            try {
                const iso = toISODate(selectedDate);
                const res = await axios.get(`${API}/appointments/availability`, {
                    params: { date: iso },
                });
                setAvailability(res.data);
            } catch (e) {
                console.error(e);
                toast.error("No se pudo cargar la disponibilidad. Intenta nuevamente.");
            } finally {
                setLoadingAvail(false);
            }
        };
        loadAvailability();
    }, [selectedDate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Por favor ingresa tu nombre");
            return;
        }
        if (!selectedProcedureId || !selectedDate || !selectedTime) {
            toast.error("Selecciona procedimiento, fecha y horario");
            return;
        }
        setSubmitting(true);
        try {
            const attribution = getAttribution();
            const res = await axios.post(`${API}/appointments`, {
                patient_name: name.trim(),
                phone: phone.trim() || null,
                procedure_id: selectedProcedureId,
                date: toISODate(selectedDate),
                time: selectedTime,
                source: attribution?.source || null,
                utm_campaign: attribution?.utm_campaign || null,
            });
            setConfirmation(res.data);
            toast.success("¡Reservación confirmada!");
            if (window.fbq) {
                window.fbq("track", "Schedule", {
                    content_name: res.data.procedure_name,
                    value: res.data.price || 0,
                    currency: "MXN",
                });
            }
        } catch (err) {
            const detail =
                err?.response?.data?.detail || "No se pudo crear la reservación. Intenta nuevamente.";
            toast.error(typeof detail === "string" ? detail : "No se pudo crear la reservación.");
            // If 409 (taken), refresh availability and go back to time step
            if (err?.response?.status === 409 && selectedDate) {
                try {
                    const res = await axios.get(`${API}/appointments/availability`, {
                        params: { date: toISODate(selectedDate) },
                    });
                    setAvailability(res.data);
                    setSelectedTime(null);
                    setStep(3);
                } catch (refreshErr) {
                    console.error("Could not refresh availability after conflict:", refreshErr);
                    /* ignore */
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const resetAll = () => {
        setStep(1);
        setSelectedProcedureId(null);
        setSelectedDate(null);
        setAvailability(null);
        setSelectedTime(null);
        setName("");
        setPhone("");
        setConfirmation(null);
    };

    const handleDownloadReceipt = async () => {
        if (!confirmation?.id) return;
        setDownloadingReceipt(true);
        try {
            const res = await axios.get(
                `${API}/appointments/${confirmation.id}/receipt.png`,
                { responseType: "blob" }
            );
            const blob = new Blob([res.data], { type: "image/png" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `comprobante_cita_${confirmation.id.slice(0, 8)}.png`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Comprobante descargado");
        } catch (err) {
            toast.error("No se pudo descargar el comprobante. Intenta nuevamente.");
        } finally {
            setDownloadingReceipt(false);
        }
    };

    const disabledDays = (day) => {
        // Closed days (per configurable schedule) and past dates are not bookable
        const dow = day.getDay();
        const group = dow === 0 ? "sunday" : dow === 6 ? "saturday" : "weekday";
        if (schedule ? !schedule[group]?.enabled : dow === 0) return true;
        return toISODate(day) < todayISO;
    };

    const hoursText = (() => {
        if (!schedule) return "Atendemos de lunes a sábado.";
        const parts = [];
        if (schedule.weekday?.enabled) {
            parts.push(`Lun–Vie de ${formatTime12(schedule.weekday.start)} a ${formatTime12(schedule.weekday.last)}`);
        }
        if (schedule.saturday?.enabled) {
            parts.push(`Sábados de ${formatTime12(schedule.saturday.start)} a ${formatTime12(schedule.saturday.last)}`);
        }
        if (schedule.sunday?.enabled) {
            parts.push(`Domingos de ${formatTime12(schedule.sunday.start)} a ${formatTime12(schedule.sunday.last)}`);
        }
        return parts.length ? `${parts.join(" · ")} (última cita).` : "Consulta disponibilidad.";
    })();

    if (confirmation) {
        return (
            <Card
                className="step-enter mx-auto max-w-2xl rounded-3xl border-border bg-card p-8 sm:p-12 soft-shadow-lg"
                data-testid="booking-confirmation"
            >
                <div className="flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-secondary/40 flex items-center justify-center mb-6">
                        <CheckCircle2 className="h-9 w-9 text-primary" strokeWidth={1.6} />
                    </div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">
                        Reservación confirmada
                    </p>
                    <h2 className="font-heading text-3xl sm:text-4xl font-medium mb-2">
                        ¡Listo, {confirmation.patient_name}!
                    </h2>
                    <p className="text-muted-foreground max-w-md">
                        Tu cita en Ciudad Enfermería ha sido agendada con éxito. Te esperamos.
                    </p>

                    <div
                        className="mt-8 w-full grid sm:grid-cols-2 gap-4 text-left"
                        data-testid="confirmation-details"
                    >
                        <div className="rounded-2xl bg-muted/60 p-5 sm:col-span-2">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                <Stethoscope className="h-4 w-4" />
                                Procedimiento
                            </div>
                            <p className="font-heading text-lg">{confirmation.procedure_name}</p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-5">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                <CalendarDays className="h-4 w-4" />
                                Fecha
                            </div>
                            <p className="font-heading text-lg">
                                {formatDateLong(new Date(`${confirmation.date}T00:00:00`))}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-5">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                <Clock className="h-4 w-4" />
                                Horario
                            </div>
                            <p className="font-heading text-lg">{formatTime12(confirmation.time)}</p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-5">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                <User className="h-4 w-4" />
                                Paciente
                            </div>
                            <p className="font-heading text-lg">{confirmation.patient_name}</p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-5">
                            <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                <Phone className="h-4 w-4" />
                                Teléfono
                            </div>
                            <p className="font-heading text-lg">{confirmation.phone || "—"}</p>
                        </div>
                        {confirmation.price != null && (
                            <div className="rounded-2xl bg-muted/60 p-5 sm:col-span-2" data-testid="confirmation-price">
                                <div className="flex items-center gap-2 text-muted-foreground text-sm uppercase tracking-wider mb-1">
                                    Costo
                                </div>
                                <p className="font-heading text-lg">
                                    {formatPrice(confirmation.price)}
                                    {(() => {
                                        const base = procedures.find(
                                            (p) => p.id === confirmation.procedure_id
                                        )?.price;
                                        return base != null && confirmation.price < base ? (
                                            <span className="ml-2 text-sm font-semibold text-amber-700">
                                                🔥 Precio promocional (antes {formatPrice(base)})
                                            </span>
                                        ) : null;
                                    })()}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 w-full text-xs text-muted-foreground text-center">
                        Folio: <span className="font-mono">{confirmation.id.slice(0, 8)}</span>
                    </div>

                    <div
                        className="mt-6 w-full rounded-2xl border border-border bg-accent/30 p-5 text-left"
                        data-testid="confirmation-clinic-info"
                    >
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2 inline-flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5" />
                            Te esperamos en
                        </p>
                        <p className="font-heading text-base leading-snug">
                            Av. Educación No. 230 Int. 1<br />
                            Col. Burócrata, C.P. 78260, San Luis Potosí, S.L.P.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <a
                                href="https://maps.app.goo.gl/fTY5BMVx8t2fq7Ys8"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm rounded-xl px-3 py-2 border border-border bg-background hover:bg-muted transition-all"
                                data-testid="confirmation-maps-link"
                            >
                                <MapPin className="h-3.5 w-3.5" />
                                Cómo llegar
                            </a>
                            <a
                                href="https://wa.me/5214443067793"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-sm rounded-xl px-3 py-2 border border-[#25D366]/30 bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-all"
                                data-testid="confirmation-whatsapp-link"
                            >
                                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="#25D366" aria-hidden>
                                    <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.51 5.26l-.999 3.648 3.978-1.043z"/>
                                </svg>
                                WhatsApp
                            </a>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                        <Button
                            onClick={handleDownloadReceipt}
                            disabled={downloadingReceipt}
                            className="rounded-2xl hover:-translate-y-0.5 transition-all"
                            data-testid="confirmation-download-receipt"
                        >
                            {downloadingReceipt ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Generando...
                                </>
                            ) : (
                                <>
                                    <FileDown className="mr-2 h-4 w-4" />
                                    Descargar comprobante
                                </>
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={resetAll}
                            className="rounded-2xl"
                            data-testid="confirmation-new-btn"
                        >
                            Reservar otra cita
                        </Button>
                    </div>
                </div>
            </Card>
        );
    }

    return (
        <Card
            className="mx-auto max-w-3xl rounded-3xl border-border bg-card p-6 sm:p-10 soft-shadow"
            data-testid="booking-card"
        >
            {promoProcedures.length > 0 && (
                <div
                    className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900"
                    data-testid="promo-banner"
                >
                    🔥 <span className="font-semibold">Promoción:</span>{" "}
                    {promoProcedures
                        .map(
                            (p) =>
                                `${p.name.replace(/\s*\(.*\)$/, "")} a $${p.promo_price} todos los días de ${formatTime12(p.promo_start)} a ${formatTime12(p.promo_end)}`
                        )
                        .join(" · ")}
                </div>
            )}
            <Stepper step={step} />

            {/* Step 1: Procedure */}
            {step === 1 && (
                <div className="step-enter">
                    <div className="text-center mb-6">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                            Paso 1 de 4
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium">
                            ¿Qué procedimiento necesitas?
                        </h2>
                        <p className="text-muted-foreground mt-2">
                            Elige el servicio que vas a agendar.
                        </p>
                    </div>
                    <ProcedureSelector
                        procedures={procedures}
                        selectedId={selectedProcedureId}
                        onSelect={setSelectedProcedureId}
                        loading={loadingProcedures}
                    />
                    <div className="mt-8 flex justify-end">
                        <Button
                            disabled={!selectedProcedureId}
                            onClick={() => {
                                if (window.fbq) {
                                    window.fbq("track", "InitiateCheckout", {
                                        content_name: selectedProcedure?.name,
                                    });
                                }
                                setStep(2);
                            }}
                            className="rounded-2xl px-6 hover:-translate-y-0.5 transition-all"
                            data-testid="step1-next-btn"
                        >
                            Siguiente
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 2: Date */}
            {step === 2 && (
                <div className="step-enter">
                    <div className="text-center mb-6">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                            Paso 2 de 4
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium">
                            ¿Qué día te gustaría venir?
                        </h2>
                        <p className="text-muted-foreground mt-2" data-testid="booking-hours-text">
                            {hoursText}
                        </p>
                    </div>
                    <div className="flex justify-center" data-testid="booking-date-select">
                        <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(d) => {
                                setSelectedDate(d);
                                setSelectedTime(null);
                            }}
                            disabled={disabledDays}
                            weekStartsOn={1}
                            className="rounded-2xl border border-border bg-background p-4"
                        />
                    </div>
                    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setStep(1)}
                            className="rounded-2xl"
                            data-testid="step2-back-btn"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Cambiar procedimiento
                        </Button>
                        <Button
                            disabled={!selectedDate}
                            onClick={() => setStep(3)}
                            className="rounded-2xl px-6 hover:-translate-y-0.5 transition-all"
                            data-testid="step2-next-btn"
                        >
                            Siguiente
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 3: Time */}
            {step === 3 && (
                <div className="step-enter">
                    <div className="text-center mb-6">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                            Paso 3 de 4
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium">
                            Elige tu horario
                        </h2>
                        <p className="text-muted-foreground mt-2" data-testid="selected-date-label">
                            {formatDateLong(selectedDate)}
                        </p>
                        {promo && (
                            <p className="mt-2 text-sm font-medium text-amber-700" data-testid="promo-hint">
                                🔥 Precio promocional de ${promo.price} en horarios de{" "}
                                {formatTime12(promo.start)} a {formatTime12(promo.end)}
                            </p>
                        )}
                    </div>
                    <TimeSlotGrid
                        availability={availability}
                        selectedTime={selectedTime}
                        onSelect={setSelectedTime}
                        loading={loadingAvail}
                        promo={promo}
                    />
                    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                        <Button
                            variant="outline"
                            onClick={() => setStep(2)}
                            className="rounded-2xl"
                            data-testid="step3-back-btn"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Cambiar fecha
                        </Button>
                        <Button
                            disabled={!selectedTime}
                            onClick={() => setStep(4)}
                            className="rounded-2xl px-6 hover:-translate-y-0.5 transition-all"
                            data-testid="step3-next-btn"
                        >
                            Continuar
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Step 4: Details */}
            {step === 4 && (
                <form className="step-enter" onSubmit={handleSubmit}>
                    <div className="text-center mb-6">
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
                            Paso 4 de 4
                        </p>
                        <h2 className="font-heading text-2xl sm:text-3xl font-medium">
                            Tus datos
                        </h2>
                        <p className="text-muted-foreground mt-2">
                            Necesitamos tu nombre y un teléfono de WhatsApp para confirmaciones.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-2xl bg-muted/60 p-4 sm:col-span-2">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
                                <Stethoscope className="h-3.5 w-3.5" />
                                Procedimiento
                            </div>
                            <p className="font-heading text-base">
                                {selectedProcedure ? selectedProcedure.name : "—"}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
                                <CalendarDays className="h-3.5 w-3.5" />
                                Fecha
                            </div>
                            <p className="font-heading text-base">
                                {formatDateLong(selectedDate)}
                            </p>
                        </div>
                        <div className="rounded-2xl bg-muted/60 p-4">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
                                <Clock className="h-3.5 w-3.5" />
                                Horario
                            </div>
                            <p className="font-heading text-base">{formatTime12(selectedTime)}</p>
                        </div>
                        {effectivePrice != null && (
                            <div
                                className={`rounded-2xl p-4 sm:col-span-2 ${
                                    isPromoSlot ? "bg-amber-50 border border-amber-300" : "bg-muted/60"
                                }`}
                                data-testid="summary-price"
                            >
                                <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
                                    Costo
                                </div>
                                <p className="font-heading text-base">
                                    {isPromoSlot && (
                                        <span className="line-through text-muted-foreground mr-2">
                                            {formatPrice(selectedProcedure.price)}
                                        </span>
                                    )}
                                    <span className={isPromoSlot ? "text-amber-800 font-semibold" : ""}>
                                        {formatPrice(effectivePrice)}
                                    </span>
                                    {isPromoSlot && (
                                        <span className="ml-2 text-xs font-semibold text-amber-700">
                                            🔥 Precio promocional
                                        </span>
                                    )}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="space-y-5">
                        <div>
                            <Label htmlFor="patient-name" className="mb-2 inline-flex items-center gap-2 text-sm">
                                <User className="h-4 w-4 text-muted-foreground" />
                                Nombre completo *
                            </Label>
                            <Input
                                id="patient-name"
                                type="text"
                                placeholder="Ej. María Fernández"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="rounded-2xl h-12 bg-background"
                                data-testid="input-patient-name"
                            />
                        </div>
                        <div>
                            <Label htmlFor="patient-phone" className="mb-2 inline-flex items-center gap-2 text-sm">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                                Teléfono / WhatsApp *
                            </Label>
                            <Input
                                id="patient-phone"
                                type="tel"
                                inputMode="tel"
                                placeholder="444 123 4567"
                                value={phone}
                                onChange={handlePhoneChange}
                                required
                                minLength={10}
                                maxLength={12}
                                className="rounded-2xl h-12 bg-background"
                                data-testid="input-patient-phone"
                            />
                            <p className="text-xs text-muted-foreground mt-1.5">
                                Lo usamos solo para recordarte o confirmar tu cita.
                            </p>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setStep(3)}
                            className="rounded-2xl"
                            data-testid="step4-back-btn"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Cambiar horario
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="rounded-2xl px-6 hover:-translate-y-0.5 transition-all"
                            data-testid="booking-submit"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Reservando...
                                </>
                            ) : (
                                <>
                                    <Stethoscope className="mr-2 h-4 w-4" />
                                    Confirmar cita
                                </>
                            )}
                        </Button>
                    </div>
                </form>
            )}
        </Card>
    );
}

export { formatDateLong, formatTime12, toISODate };
